import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import {
  selectPatchDataRevision,
} from '../../../features/patches/patchesSlice';
import { useFeatureFlag } from '../../../services/optimizely/optimizelyHooks';
import { FeatureFlags } from '../../../constants/FeatureFlags';
import { getPatch, getNamedPrices } from '../../../services/api';
import { extractCurrentNamedPrices } from '../../../features/patches/utils/namedPriceUtils';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { PatchTypeSelector } from '../PatchTypeSelector';
import StoreOperationsDialog from '../../../features/patches/components/StoreOperationsDialog';
import NamedPriceEditor from '../../../features/patches/components/NamedPriceEditor';
// Note: PendingChangesView import removed as it's no longer used in this component
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Alert,
  Divider,
  Snackbar,
  IconButton,
  Tooltip,
  Button,
  Collapse,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { PatchType, Store } from '../../../types';

// Styled component for loading spinner
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const LoadingSpinner = styled(Box)(({ theme }) => ({
  display: 'inline-block',
  width: '20px',
  height: '20px',
  border: '2px solid #f3f3f3',
  borderTop: '2px solid #3498db',
  borderRadius: '50%',
  animation: `${spin} 2s linear infinite`,
}));

type PatchListProps = {
  stores: Store[];
  storeNumbers: string[];
  patchType: PatchType;
  handlePatchTypeChange: (patchType: PatchType) => void;
  getPatchIdsForType?: (patchType: PatchType) => string[];
  getPatchDataForType?: (patchType: PatchType) => any[];
};

const PatchList: React.FC<PatchListProps> = ({
  stores,
  storeNumbers,
  patchType,
  handlePatchTypeChange,
  getPatchIdsForType,
  getPatchDataForType,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [patchCache, setPatchCache] = useState<Record<string, any>>({});
  const [loadingPatches, setLoadingPatches] = useState<Set<string>>(new Set());
  const [expandedPatchIds, setExpandedPatchIds] = useState<Set<string>>(new Set());
  /** Which patch's operations are open in the full-page editor, if any. */
  const [operationsDialog, setOperationsDialog] = useState<{
    patchId: string;
    patchName: string;
    storeNumber: string;
  } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const errorTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const patchDataRevision = useSelector(selectPatchDataRevision);
  const { isEnabled: patchEditEnabled } = useFeatureFlag(FeatureFlags.PATCH_EDIT_ENABLED);
  const { isEnabled: namedPricingEnabled } = useFeatureFlag(FeatureFlags.NAMED_PRICING_ENABLED);

  // patch_edit_enabled is the master gate for this table: named pricing adds a column to it, so it
  // cannot show up in an environment where the patch list itself is turned off.
  const namedPricesColumnEnabled = patchEditEnabled && namedPricingEnabled;

  // Patch Name, Patch ID, Store, FZ Code, Total Operations, plus whatever the flags add. Drives the
  // colSpan of the expanded named price row.
  const tableColumnCount = 5 + (namedPricesColumnEnabled ? 1 : 0) + (patchEditEnabled ? 1 : 0);

  /**
   * The selection, as a value rather than an array identity.
   *
   * Callers build `storeNumbers` inline - `Step3ReviewAndSubmit` derives it from `selectedStores` on
   * every render - so its identity changes whenever the page re-renders for an unrelated reason, and it
   * re-renders on every pending change. Keying the effects below on the array itself therefore dropped
   * the loaded patch and closed the operations editor each time a user queued an edit in it.
   */
  const storeKey = useMemo(() => storeNumbers.join('|'), [storeNumbers]);

  // Named price names are resolved from the org catalog rather than the patch payload, which is not
  // guaranteed to embed them. Same query key and staleTime as NamedPriceSelect, so the small
  // org-wide list is fetched once and shared.
  const { data: namedPriceCatalog } = useQuery('namedPrices', getNamedPrices, {
    staleTime: 5 * 60 * 1000,
    enabled: namedPricesColumnEnabled,
  });

  const resolveNamedPriceName = useMemo(() => {
    const nameById = new Map(
      (namedPriceCatalog || []).map((namedPrice) => [namedPrice.namedPriceId, namedPrice.name])
    );
    return (namedPriceId: string) => nameById.get(namedPriceId);
  }, [namedPriceCatalog]);

  const toggleNamedPricesExpanded = useCallback((patchId: string) => {
    setExpandedPatchIds((prev) => {
      const next = new Set(prev);
      if (!next.delete(patchId)) next.add(patchId);
      return next;
    });
  }, []);

  // Error message cleanup function
  const clearErrorMessage = () => {
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
    setErrorMessage(null);
  };

  // Handle patch data fetching and caching with proper error handling
  const handleLoadPatchDetails = useCallback(async (patchId: string) => {
    // Validate input
    if (!patchId || typeof patchId !== 'string') {
      setErrorMessage('Invalid patch ID provided');
      return;
    }

    // Only guard against a second fetch of the same patch while one is in flight. A patch already in
    // the cache must still be refetchable: the button offers a reload once loaded, and the cache goes
    // stale the moment a submission lands, so refusing here left no way to re-read a patch.
    if (loadingPatches.has(patchId)) {
      return;
    }

    setLoadingPatches(prev => new Set(prev).add(patchId));
    
    try {
      const patchData = await getPatch(patchId);
      
      // Validate response data
      if (!patchData || typeof patchData !== 'object') {
        throw new Error('Invalid patch data received');
      }

      setPatchCache(prev => ({
        ...prev,
        [patchId]: patchData
      }));
    } catch (error) {
      console.error(`Failed to load patch details for ${patchId}:`, error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
      setErrorMessage(`Failed to load patch details: ${errorMsg}`);
      
      // Clear any existing timeout
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      // Clear error message after 5 seconds
      errorTimeoutRef.current = setTimeout(() => {
        setErrorMessage(null);
        errorTimeoutRef.current = null;
      }, 5000);
    } finally {
      setLoadingPatches(prev => {
        const newSet = new Set(prev);
        newSet.delete(patchId);
        return newSet;
      });
    }
  }, [loadingPatches]);

  // Removed useExtractedData state, not needed
  const [filteredPatchData, setFilteredPatchData] = useState<any[]>([]);
  
  // Memoize operation count calculation to prevent unnecessary recalculations
  const getOperationCount = useMemo(() => (patch: any) => {
    if (patchCache[patch.patchId]?.patchOperations?.edges) {
      return patchCache[patch.patchId].patchOperations.edges.length;
    }
    return null;
  }, [patchCache]);

  // Current named price overrides for a store, available once its patch details have been loaded
  const getCurrentNamedPrices = useMemo(() => (patch: any) => {
    const patchData = patchCache[patch.patchId];
    return patchData ? extractCurrentNamedPrices(patchData, resolveNamedPriceName) : null;
  }, [patchCache, resolveNamedPriceName]);

  // Memoize filtered patches to prevent unnecessary re-renders
  const memoizedPatches = useMemo(() => {
    return filteredPatchData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredPatchData, page, rowsPerPage]);
  const [patchFranchiseCodes] = useState<Record<string, string>>({});
  
  // Auto-reload patch list on patchType or storeNumbers change
  useEffect(() => {
    if (getPatchDataForType) {
      let patchesForType = getPatchDataForType(patchType);

      // Prefer short patch name (e.g., 000000_SetProdPrices) if available
      const getShortPatchName = (patch: any) => {
        if (patch.name && /^\d+_/.test(patch.name)) return patch.name;
        if (patch.patchName && /^\d+_/.test(patch.patchName)) return patch.patchName;
        return patch.name || patch.patchName || '';
      };

      // Sort patches to prefer short name first
      patchesForType = patchesForType.slice().sort((a, b) => {
        const aShort = /^\d+_/.test(getShortPatchName(a));
        const bShort = /^\d+_/.test(getShortPatchName(b));
        if (aShort && !bShort) return -1;
        if (!aShort && bShort) return 1;
        return getShortPatchName(a).localeCompare(getShortPatchName(b));
      });

  setFilteredPatchData(patchesForType);
    } else {
  setFilteredPatchData([]);
    }
  }, [getPatchDataForType, patchType, storeKey]);

  // Use filtered patch data as the patches array
  const patches = filteredPatchData;

  // Clean up in-flight work when the component unmounts or filters change
  useEffect(() => {
    return () => {
      // Clean up any pending error timeout
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = null;
      }
      // Clear any pending async operations
      setLoadingPatches(new Set());
      setErrorMessage(null);
    };
  }, [patchType, storeKey]);

  /**
   * Drops every locally held patch payload, along with the views built from one.
   *
   * The open editor has to close with it: it renders from `patchCache`, so leaving it up would show a
   * patch that is no longer loaded. Pending edits survive - they live in Redux, not here.
   */
  const dropLoadedPatches = useCallback(() => {
    setPatchCache({});
    setLoadingPatches(new Set());
    setExpandedPatchIds(new Set());
    setOperationsDialog(null);
  }, []);

  // Clean up cache when filters change to prevent memory leaks
  useEffect(() => {
    dropLoadedPatches();
  }, [patchType, storeKey, dropLoadedPatches]);

  /**
   * A submission has changed these patches on the server, so the cached copies are now wrong.
   *
   * Skipped on mount - revision 0 means nothing has been submitted yet, and clearing an empty cache
   * would only close an editor the user had just opened.
   */
  const lastSeenRevision = React.useRef(patchDataRevision);
  useEffect(() => {
    if (patchDataRevision === lastSeenRevision.current) return;
    lastSeenRevision.current = patchDataRevision;
    dropLoadedPatches();
  }, [patchDataRevision, dropLoadedPatches]);

  // No menu channel or fetch patches handler needed

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  // Always show the patch list, auto-loads on patchType/storeNumbers change


  const patchCount = patches.length;

  // No patches found
  if (patchCount === 0) {
    return (
      <Paper sx={{ width: '100%', mb: 2, p: 2 }} data-testid='noPatchesFoundPaper'>
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant='subtitle2' gutterBottom>
              Patch Type:
            </Typography>
            <PatchTypeSelector
              value={patchType}
              onChange={handlePatchTypeChange}
              variant='select'
            />
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Alert severity='info'>
          No patches found for the selected stores and patch type.
        </Alert>
      </Paper>
    );
  }

  // Render the patches in a table with batch status indicator
  // If using new pendingOps structure, pass enriched ops for display
  // TODO: Replace this with the correct pendingOps from useBulkUpdate if available in this scope
  // For now, fallback to old logic if not refactored yet
  return (
    <Box>
      <Paper sx={{ width: '100%', mb: 2 }} data-testid='patchListPaper'>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant='subtitle2' gutterBottom>
                Patch Type:
              </Typography>
              <PatchTypeSelector
                value={patchType}
                onChange={handlePatchTypeChange}
                variant='select'
              />
            </Box>
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <TableContainer>
          <Table size='medium'>
            <TableHead>
              <TableRow>
                <TableCell>Patch Name</TableCell>
                <TableCell>Patch ID</TableCell>
                <TableCell>Store</TableCell>
                <TableCell>FZ Code</TableCell>
                <TableCell>Total Operations</TableCell>
                {namedPricesColumnEnabled && <TableCell>Named Prices</TableCell>}
                {patchEditEnabled && <TableCell>Details</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {memoizedPatches.map((patch: any) => {
                const totalOperations = getOperationCount(patch);
                const currentNamedPrices = namedPricesColumnEnabled ? getCurrentNamedPrices(patch) : null;
                const isLoading = loadingPatches.has(patch.patchId);
                const isLoaded = Boolean(patchCache[patch.patchId]);
                const isExpanded = expandedPatchIds.has(patch.patchId);
                let store = stores.find((s) =>
                  s.assignedMenuDefinitions?.some((menuDef) =>
                    menuDef.patches?.some((p) => p.patch.patchId === patch.patchId)
                  )
                );
                if (!store && patch.storeNumber) {
                  store = stores.find((s) => s.storeNumber === patch.storeNumber);
                }
                if (!store && patch.storeNumbers && patch.storeNumbers.length > 0) {
                  store = stores.find((s) => s.storeNumber === patch.storeNumbers[0]);
                }
                let extractedStoreNumber: string | null = null;
                if (patch.name) {
                  const patchNameMatch = patch.name.match(/^(\d+)_/);
                  if (patchNameMatch && patchNameMatch[1]) {
                    extractedStoreNumber = patchNameMatch[1];
                  }
                }
                const storeNumber =
                  store?.storeNumber ||
                  patch.storeNumber ||
                  extractedStoreNumber ||
                  (patch.storeNumbers && patch.storeNumbers.length > 0 ? patch.storeNumbers[0] : 'N/A');
                const franchiseCode = patchFranchiseCodes[patch.patchId] || 'N/A';
                const getShortPatchName = (patch: any) => {
                  if (patch.name && /^\d+_/.test(patch.name)) return patch.name;
                  if (patch.patchName && /^\d+_/.test(patch.patchName)) return patch.patchName;
                  return patch.name || patch.patchName || `Patch ${patch.patchId}`;
                };
                return (
                  <React.Fragment key={patch.patchId}>
                  <TableRow>
                    <TableCell>{getShortPatchName(patch)}</TableCell>
                    <TableCell>{patch.patchId}</TableCell>
                    <TableCell>
                      {storeNumber !== 'N/A' ? <Chip label={storeNumber} size='small' variant='outlined' /> : 'N/A'}
                    </TableCell>
                    <TableCell>{franchiseCode}</TableCell>
                    <TableCell>
                      {/* Loaded patches expose their operations for direct, store-level editing. */}
                      {isLoading ? (
                        <LoadingSpinner component='span' />
                      ) : totalOperations === null ? (
                        <Typography variant='body2'>—</Typography>
                      ) : patchEditEnabled ? (
                        <Button
                          size='small'
                          onClick={() =>
                            setOperationsDialog({
                              patchId: patch.patchId,
                              patchName: getShortPatchName(patch),
                              storeNumber,
                            })
                          }
                          data-testid={`manageOperations-${patch.patchId}`}
                          aria-label={`Manage ${totalOperations} operations for store ${storeNumber}`}
                        >
                          {totalOperations.toLocaleString()}
                        </Button>
                      ) : (
                        <Typography variant='body2'>{totalOperations.toLocaleString()}</Typography>
                      )}
                    </TableCell>
                    {namedPricesColumnEnabled && (
                      <TableCell>
                        {/* Only known once patch details are loaded - same on-demand load as the operation count */}
                        {currentNamedPrices === null ? (
                          isLoading ? <LoadingSpinner component='span' /> : '—'
                        ) : currentNamedPrices.length === 0 ? (
                          <Typography variant='body2' color='textSecondary'>None</Typography>
                        ) : (
                          <Button
                            size='small'
                            onClick={() => toggleNamedPricesExpanded(patch.patchId)}
                            endIcon={
                              <KeyboardArrowDownIcon
                                fontSize='small'
                                sx={{
                                  transition: 'transform 150ms',
                                  transform: isExpanded ? 'rotate(180deg)' : 'none',
                                }}
                              />
                            }
                            aria-expanded={isExpanded}
                            aria-label={`${isExpanded ? 'Hide' : 'Show'} ${currentNamedPrices.length} named prices for patch ${patch.patchId}`}
                          >
                            {currentNamedPrices.length}
                          </Button>
                        )}
                      </TableCell>
                    )}
                    {patchEditEnabled && (
                      <TableCell>
                        {/* Loading is always user-initiated: patch payloads are large, so nothing fetches on its own */}
                        <Tooltip
                          title={
                            isLoading
                              ? 'Loading patch details...'
                              : isLoaded
                                ? 'Patch details loaded - click to reload'
                                : 'Load patch details'
                          }
                        >
                          <span>
                            <IconButton
                              size='small'
                              color={isLoaded && !isLoading ? 'success' : 'default'}
                              onClick={() => handleLoadPatchDetails(patch.patchId)}
                              disabled={isLoading}
                              // An icon alone is not a name. Kept in step with the tooltip above.
                              aria-label={
                                isLoading
                                  ? `Loading patch details for store ${storeNumber}`
                                  : isLoaded
                                    ? `Reload patch details for store ${storeNumber}`
                                    : `Load patch details for store ${storeNumber}`
                              }
                              sx={{
                                opacity: isLoading ? 0.5 : 1,
                                '&:hover': {
                                  backgroundColor: 'action.hover'
                                }
                              }}
                            >
                              {isLoading ? (
                                <LoadingSpinner component='span' />
                              ) : isLoaded ? (
                                <RefreshIcon fontSize='small' />
                              ) : (
                                <FileDownloadOutlinedIcon fontSize='small' />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                  {namedPricesColumnEnabled && currentNamedPrices !== null && currentNamedPrices.length > 0 && (
                    <TableRow>
                      {/* Reads from the already-loaded patch - expanding never fetches */}
                      <TableCell colSpan={tableColumnCount} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                        <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                          <NamedPriceEditor
                            patchId={patch.patchId}
                            storeNumber={storeNumber}
                            currentNamedPrices={currentNamedPrices}
                            catalog={namedPriceCatalog}
                            editable={namedPricesColumnEnabled}
                          />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component='div'
          count={patches.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Full-page operations editor for one store's patch. Renders from patchCache - never fetches. */}
      {operationsDialog && (
        <StoreOperationsDialog
          open
          onClose={() => setOperationsDialog(null)}
          patchId={operationsDialog.patchId}
          patchName={operationsDialog.patchName}
          storeNumber={operationsDialog.storeNumber}
          patchData={patchCache[operationsDialog.patchId]}
        />
      )}

      {/* Error Toast */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={5000}
        onClose={clearErrorMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={clearErrorMessage}
          severity='error'
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PatchList;
